import {
  ChartingLibraryFeatureset,
  ChartingLibraryWidgetOptions,
} from 'charting_library/charting_library';
import { CHARTS_API } from 'constants/api';
import { isCI, isDevnet, isProd } from 'constants/constants';
// import { isMobileDeviceOnly } from 'helpers';

import datafeed from './newDatafeed';
// import SaveLoadAdapter from './newSaveLoadAdapter';
import saveLoadAdapter from './saveLoadAdapter';

const isProdBackup = process.env.REACT_APP_ENV === 'production-backup';

const prependAppToUrl = url => (isProdBackup || isCI ? `/${url}` : `/app/${url}`);

const chartConfig = ({
  symbol,
  interval,
  themeObj,
  userId,
  activeTab,
  activeTheme,
  timeframe,
  contractType,
  isNewTab,
}): ChartingLibraryWidgetOptions => {
  // eslint-disable-next-line no-console
  // console.log('DEBUG ,chartConfig', symbol, interval, themeObj, userId, activeTab);
  let timezone = Intl.DateTimeFormat().resolvedOptions().timeZone;
  if (timezone && timezone.toLowerCase() === 'asia/calcutta') {
    timezone = 'Asia/Kolkata';
  }

  const userIdPrefix = isProd
    ? `delta-${activeTab}-200323`
    : `delta-testnet-${activeTab}-200323`;

  // const saveLoadAdapter = new SaveLoadAdapter();

  const disabledFeatures: ChartingLibraryFeatureset[] = [
    'remove_library_container_border',
    'header_undo_redo',
    'header_saveload',
    'border_around_the_chart',
    'items_favoriting',
    'display_market_status',
    'symbol_search_hot_key',
  ];

  // Only disable symbol search features when NOT in new tab mode
  if (!isNewTab) {
    disabledFeatures.push('header_symbol_search');
  }

  // if (isMobileDeviceOnly()) {
  //   disabledFeatures.push('header_fullscreen_button');
  // }

  return {
    debug: isDevnet || isCI,
    symbol,
    interval,
    container: 'tv_chart_container',
    datafeed: datafeed(contractType),
    library_path: prependAppToUrl('charting_library/'),
    custom_css_url: prependAppToUrl(`charting_library/customStyles.css?v=${Date.now()}`),
    locale: 'en',
    disabled_features: disabledFeatures,
    enabled_features: [
      'hide_last_na_study_output',
      'dont_show_boolean_study_arguments',
      'side_toolbar_in_fullscreen_mode',
      'save_chart_properties_to_local_storage',
      'volume_force_overlay',
      'chart_template_storage',
      'create_volume_indicator_by_default',
      'use_localstorage_for_settings',
      'header_screenshot',
      'hide_object_tree_and_price_scale_exchange_label',
      'auto_enable_symbol_labels',
      'header_in_fullscreen_mode',
      'adaptive_logo',
      'show_symbol_logo_in_legend',
      'show_symbol_logos',
      'horz_touch_drag_scroll',
      'iframe_loading_compatibility_mode',
      'show_zoom_and_move_buttons_on_touch',
      'symbol_info',
    ],
    load_last_chart: true,
    save_load_adapter: saveLoadAdapter,
    charts_storage_url: CHARTS_API.STORAGE,
    charts_storage_api_version: '1.1',
    auto_save_delay: 1,
    autosize: true,
    timezone,
    timeframe,
    theme: activeTheme,
    brokerConfig: {
      configFlags: { supportModifyOrderPrice: true, supportEditAmount: true },
    },
    custom_font_family: 'Aileron',
    drawings_access: {
      type: 'black',
      tools: [],
    },
    client_id: 'delta.exchange',
    user_id: `${userIdPrefix}-${userId || 'public'}`,
    loading_screen: {
      backgroundColor: themeObj.primaryBackground,
      foregroundColor: themeObj.primaryTheme,
    },
    time_frames: [],
    favorites: {
      chartTypes: ['Candles'],
    },
    overrides: {
      // 'paneProperties.backgroundType': 'solid',
      'paneProperties.background': themeObj.primaryBackground,
      'paneProperties.vertGridProperties.color': themeObj.separator,
      'paneProperties.horzGridProperties.color': themeObj.separator,
      'paneProperties.vertGridProperties.style': 1,
      'paneProperties.horzGridProperties.style': 1,
      'paneProperties.bottomMargin': 5,

      // Select chart type
      'mainSeriesProperties.style': 1,
      'mainSeriesProperties.showCountdown': true,
      // Bar style
      'mainSeriesProperties.barStyle.upColor': themeObj.positive,
      'mainSeriesProperties.barStyle.downColor': themeObj.negative,
      'mainSeriesProperties.barStyle.barColorsOnPrevClose': false,
      'mainSeriesProperties.barStyle.dontDrawOpen': true,
      // Candle Style
      'mainSeriesProperties.candleStyle.upColor': themeObj.positive,
      'mainSeriesProperties.candleStyle.borderUpColor': themeObj.positive,
      'mainSeriesProperties.candleStyle.downColor': themeObj.negative,
      'mainSeriesProperties.candleStyle.borderDownColor': themeObj.negative,
      'mainSeriesProperties.candleStyle.drawWick': true,
      'mainSeriesProperties.candleStyle.wickUpColor': themeObj.positive,
      'mainSeriesProperties.candleStyle.wickDownColor': themeObj.negative,
      'mainSeriesProperties.candleStyle.barColorsOnPrevClose': false,
      // Hollow Candle Style
      'mainSeriesProperties.hollowCandleStyle.upColor': themeObj.positive,
      'mainSeriesProperties.hollowCandleStyle.borderUpColor': themeObj.positive,
      'mainSeriesProperties.hollowCandleStyle.downColor': themeObj.negative,
      'mainSeriesProperties.hollowCandleStyle.borderDownColor': themeObj.negative,
      'mainSeriesProperties.hollowCandleStyle.drawWick': false,
      // Heikin Ashi styles
      'mainSeriesProperties.haStyle.upColor': themeObj.positive,
      'mainSeriesProperties.haStyle.downColor': themeObj.negative,
      'mainSeriesProperties.haStyle.drawWick': true,
      'mainSeriesProperties.haStyle.drawBorder': true,
      'mainSeriesProperties.haStyle.borderColor': '',
      'mainSeriesProperties.haStyle.borderUpColor': themeObj.positive,
      'mainSeriesProperties.haStyle.borderDownColor': themeObj.negative,
      'mainSeriesProperties.haStyle.wickUpColor': themeObj.positive,
      'mainSeriesProperties.haStyle.wickDownColor': themeObj.negative,
      'mainSeriesProperties.haStyle.barColorsOnPrevClose': false,
      // Area Style
      'mainSeriesProperties.areaStyle.color1': themeObj.positive,
      'mainSeriesProperties.areaStyle.color2': themeObj.negative,
      'mainSeriesProperties.areaStyle.linecolor': themeObj.negative,
      // Line styles
      'mainSeriesProperties.lineStyle.color': themeObj.negative,
      'mainSeriesProperties.lineStyle.linestyle': 0,
      'mainSeriesProperties.lineStyle.linewidth': 1,
      // Baseline styles
      'mainSeriesProperties.baselineStyle.baselineColor': themeObj.primaryText,
      'mainSeriesProperties.baselineStyle.topFillColor1': themeObj.positive,
      'mainSeriesProperties.baselineStyle.topFillColor2': 'rgba( 83, 185, 135, 0.1)',
      'mainSeriesProperties.baselineStyle.bottomFillColor1': themeObj.negative,
      'mainSeriesProperties.baselineStyle.bottomFillColor2': 'rgba( 235, 77, 92, 0.1)',
      'mainSeriesProperties.baselineStyle.topLineColor': themeObj.positive,
      'mainSeriesProperties.baselineStyle.bottomLineColor': themeObj.negative,
      // Legend properties
      'paneProperties.legendProperties.showSeriesTitle': false,
      'paneProperties.legendProperties.showSeriesOHLC': true,
      'paneProperties.legendProperties.showStudyTitles': true,
      'paneProperties.legendProperties.showStudyValues': true,
      'scalesProperties.fontSize': 12,
      'scalesProperties.showSymbolLabels': false,
      'scalesProperties.showStudyLastValue': true,
      'symbolWatermarkProperties.color': 'rgba(110, 110, 110, 0.1)',
      volumePaneSize: 'small',
      'scalesProperties.lineColor': themeObj.separator,
      'scalesProperties.textColor': themeObj.secondaryText,
      'paneProperties.crossHairProperties.color': themeObj.secondaryText,
      'mainSeriesProperties.statusViewStyle.symbolTextSource': 'ticker',
      'paneProperties.topMargin': 0,
    },
    studies_overrides: {
      'volume.volume.color.0': themeObj.negative,
      'volume.volume.color.1': themeObj.positive,
      'volume.precision': 2,
    },
    symbol_search_request_delay: 1000,
  };
};

export default chartConfig;
